import { computed } from 'vue'
import { getAuth } from 'firebase/auth'
import { useImpersonateStore } from '~/stores/impersonate'
import { useUserStore } from '~/stores/useUserStore'

let customerIdInitialization: Promise<string | null> | null = null

function resolveEffectiveCustomerId(
  userStore: ReturnType<typeof useUserStore>,
  impersonateStore: ReturnType<typeof useImpersonateStore>,
): string {
  if (impersonateStore.isImpersonating && impersonateStore.impersonatedCustomerId) {
    return impersonateStore.impersonatedCustomerId
  }
  return userStore.customerId
}

export function useEffectiveCustomerId() {
  const userStore = useUserStore()
  const impersonateStore = useImpersonateStore()

  return computed(() => resolveEffectiveCustomerId(userStore, impersonateStore))
}

async function initializeEffectiveCustomerId(): Promise<string | null> {
  const userStore = useUserStore()
  const impersonateStore = useImpersonateStore()

  const auth = getAuth()
  await auth.authStateReady()
  if (!auth.currentUser) {
    return null
  }

  if (impersonateStore.isImpersonating && !impersonateStore.impersonatedCustomerId) {
    await impersonateStore.fetchContext()
  }

  if (!resolveEffectiveCustomerId(userStore, impersonateStore)) {
    await userStore.fetchUserData()
  }

  const customerId = resolveEffectiveCustomerId(userStore, impersonateStore)
  return customerId || null
}

export async function ensureEffectiveCustomerId(): Promise<string | null> {
  const userStore = useUserStore()
  const impersonateStore = useImpersonateStore()
  const existingCustomerId = resolveEffectiveCustomerId(userStore, impersonateStore)

  if (existingCustomerId) {
    return existingCustomerId
  }

  if (!customerIdInitialization) {
    customerIdInitialization = initializeEffectiveCustomerId()
  }

  const initialization = customerIdInitialization
  try {
    return await initialization
  }
  finally {
    if (customerIdInitialization === initialization) {
      customerIdInitialization = null
    }
  }
}
